import * as THREE from 'three';

/**
 * Procedural PBR texture generation.
 *
 * Design notes
 * ------------
 * - Everything is authored in LINEAR albedo and encoded to sRGB on pack, so the
 *   preset numbers can be checked directly against real-world reflectance
 *   tables (concrete 0.30-0.45, asphalt 0.05-0.10, dry sand 0.30-0.40, ...).
 * - Each surface is built from an explicit detail hierarchy: macro (metres),
 *   meso (centimetres), micro (millimetres), plus *structure* — form-tie holes,
 *   pour lines, aggregate stones, crack networks, weld seams, trowel arcs.
 *   Structure is what separates a scan from an fbm smear.
 * - Low-frequency octaves are evaluated at a reduced resolution and bilinearly
 *   upsampled. A perlin octave with N lattice cells carries no detail above
 *   ~6N samples, so this is free quality-wise and turns an O(size^2 * octaves)
 *   build into something dominated by the single highest octave.
 * - A shared millimetre-scale detail normal/albedo layer is injected into
 *   MeshStandardMaterial via onBeforeCompile and faded out with view distance,
 *   so surfaces stay sharp at 30cm without aliasing at 30m.
 *
 * ORM packing follows glTF: R = AO, G = roughness, B = metalness.
 */

/* ------------------------------------------------------------------ math -- */

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const mix = (a, b, t) => a + (b - a) * t;
const sstep = (e0, e1, x) => {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
};
const frac = (x) => x - Math.floor(x);

function mulberry(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Deterministic scalar hash for per-cell attributes. Integer bit-mixer rather
 * than the usual fract(sin(...)) — this runs a few million times per surface
 * and Math.sin dominated the build otherwise.
 */
function hash1(x) {
  let h = ((x * 16777216) | 0) ^ 0x9e3779b9;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

// Linear -> sRGB byte, via a LUT (the pow() is the hot spot otherwise).
const SRGB_LUT = new Uint8Array(4097);
for (let i = 0; i <= 4096; i++) {
  const l = i / 4096;
  const s = l <= 0.0031308 ? l * 12.92 : 1.055 * Math.pow(l, 1 / 2.4) - 0.055;
  SRGB_LUT[i] = Math.round(clamp(s, 0, 1) * 255);
}
const toSRGB = (l) => SRGB_LUT[(clamp(l, 0, 1) * 4096) | 0];

/* ----------------------------------------------------------------- noise -- */

/** Tileable perlin with independent X/Y periods. */
function makeNoise(seed) {
  const perm = new Uint8Array(512);
  const rand = mulberry(seed);
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  for (let i = 255; i > 0; i--) {
    const j = (rand() * (i + 1)) | 0;
    const t = p[i]; p[i] = p[j]; p[j] = t;
  }
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255];

  const fade = (t) => t * t * t * (t * (t * 6 - 15) + 10);
  const grad = (h, x, y) => {
    switch (h & 3) {
      case 0: return x + y;
      case 1: return -x + y;
      case 2: return x - y;
      default: return -x - y;
    }
  };

  return function noise(x, y, px, py) {
    const xi = Math.floor(x), yi = Math.floor(y);
    const xf = x - xi, yf = y - yi;
    const u = fade(xf), v = fade(yf);
    const X0 = (((xi % px) + px) % px) & 255, X1 = (((xi + 1) % px + px) % px) & 255;
    const Y0 = (((yi % py) + py) % py) & 255, Y1 = (((yi + 1) % py + py) % py) & 255;
    const aa = perm[X0 + perm[Y0]], ba = perm[X1 + perm[Y0]];
    const ab = perm[X0 + perm[Y1]], bb = perm[X1 + perm[Y1]];
    const l0 = grad(aa, xf, yf) + (grad(ba, xf - 1, yf) - grad(aa, xf, yf)) * u;
    const l1 = grad(ab, xf, yf - 1) + (grad(bb, xf - 1, yf - 1) - grad(ab, xf, yf - 1)) * u;
    return l0 + (l1 - l0) * v;
  };
}

const pow2ceil = (n) => 1 << Math.ceil(Math.log2(Math.max(2, n)));

/** Bilinear (wrapping) upsample-accumulate of an rx*ry field into a dx*dy one. */
function upAddRect(out, dx, dy, src, rx, ry, amp) {
  if (rx === dx && ry === dy) {
    for (let i = 0; i < out.length; i++) out[i] += src[i] * amp;
    return;
  }
  const sx = rx / dx, sy = ry / dy;
  for (let y = 0; y < dy; y++) {
    const gy = y * sy;
    let y0 = gy | 0; const ty = gy - y0; y0 %= ry;
    const y1 = (y0 + 1) % ry;
    const r0 = y0 * rx, r1 = y1 * rx, o = y * dx;
    for (let x = 0; x < dx; x++) {
      const gx = x * sx;
      let x0 = gx | 0; const tx = gx - x0; x0 %= rx;
      const x1 = (x0 + 1) % rx;
      const a = src[r0 + x0];
      const t0 = a + (src[r0 + x1] - a) * tx;
      const c = src[r1 + x0];
      const t1 = c + (src[r1 + x1] - c) * tx;
      out[o + x] += (t0 + (t1 - t0) * ty) * amp;
    }
  }
}

const upAdd = (out, size, src, rx, ry, amp) => upAddRect(out, size, size, src, rx, ry, amp);

/**
 * Tileable fbm. Every octave is evaluated at ~6 samples per lattice cell —
 * beyond that a perlin octave carries no information — accumulated into a
 * pyramid buffer sized to the *finest* octave, and upsampled to full
 * resolution exactly once. That last part matters: doing it per octave made
 * the full-res bilinear pass the dominant cost of the whole build.
 */
function fbmField(noise, size, n, fx, fy, oct, gain, lac, capRes) {
  const cap = Math.min(size, capRes || size);
  const plan = [];
  let a = fx, b = fy, Rx = 8, Ry = 8;
  for (let i = 0; i < oct; i++) {
    const ffx = Math.max(1, Math.round(a)), ffy = Math.max(1, Math.round(b));
    const rx = Math.min(cap, Math.max(8, pow2ceil(ffx * 6)));
    const ry = Math.min(cap, Math.max(8, pow2ceil(ffy * 6)));
    plan.push(ffx, ffy, rx, ry);
    if (rx > Rx) Rx = rx;
    if (ry > Ry) Ry = ry;
    a *= lac; b *= lac;
  }
  const acc = new Float32Array(Rx * Ry);
  let amp = 1, norm = 0;
  for (let i = 0; i < oct; i++) {
    const ffx = plan[i * 4], ffy = plan[i * 4 + 1];
    const rx = plan[i * 4 + 2], ry = plan[i * 4 + 3];
    const tmp = new Float32Array(rx * ry);
    const sx = ffx / rx, sy = ffy / ry, ox = i * 37.13, oy = i * 19.71;
    for (let y = 0; y < ry; y++) {
      const ny = y * sy + oy, row = y * rx;
      for (let x = 0; x < rx; x++) tmp[row + x] = noise(x * sx + ox, ny, ffx, ffy);
    }
    upAddRect(acc, Rx, Ry, tmp, rx, ry, amp);
    norm += amp; amp *= gain;
  }
  const out = new Float32Array(n);
  upAddRect(out, size, size, acc, Rx, Ry, 1 / norm);
  return out;
}

/* ------------------------------------------------------------- toolchest -- */

const wrapi = (i, n) => ((i % n) + n) % n;

/** Separable box blur with wrap, run `passes` times to approximate a gaussian. */
function blur(src, size, radius, passes = 2) {
  const n = size * size;
  const a = Float32Array.from(src);
  const b = new Float32Array(n);
  const w = radius * 2 + 1, inv = 1 / w;
  for (let p = 0; p < passes; p++) {
    for (let y = 0; y < size; y++) {
      const o = y * size;
      let s = 0;
      for (let k = -radius; k <= radius; k++) s += a[o + wrapi(k, size)];
      for (let x = 0; x < size; x++) {
        b[o + x] = s * inv;
        s += a[o + wrapi(x + radius + 1, size)] - a[o + wrapi(x - radius, size)];
      }
    }
    for (let x = 0; x < size; x++) {
      let s = 0;
      for (let k = -radius; k <= radius; k++) s += b[wrapi(k, size) * size + x];
      for (let y = 0; y < size; y++) {
        a[y * size + x] = s * inv;
        s += b[wrapi(y + radius + 1, size) * size + x] - b[wrapi(y - radius, size) * size + x];
      }
    }
  }
  return a;
}

/**
 * Tileable Worley / cellular noise. Returns F1, F2 (in cell units) and cell id.
 * Evaluated at ~6 samples per cell and upsampled, which is where the detail
 * actually lives and keeps the 9-neighbour search off the full-res grid.
 */
function worley(seed, fullSize, cx, cy, jitter = 1) {
  const cells = Math.max(cx, cy);
  const size = Math.min(fullSize, Math.max(64, Math.ceil(cells * 4 / 16) * 16));
  const rnd = mulberry(seed);
  const n = cx * cy;
  const px = new Float32Array(n), py = new Float32Array(n), cid = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    px[i] = (i % cx) + 0.5 + (rnd() - 0.5) * jitter;
    py[i] = ((i / cx) | 0) + 0.5 + (rnd() - 0.5) * jitter;
    cid[i] = rnd();
  }
  const f1 = new Float32Array(size * size);
  const f2 = new Float32Array(size * size);
  const id = new Float32Array(size * size);
  const sx = cx / size, sy = cy / size;
  for (let y = 0; y < size; y++) {
    const gy = y * sy;
    const cyi = Math.min(cy - 1, gy | 0);
    const o = y * size;
    for (let x = 0; x < size; x++) {
      const gx = x * sx;
      const cxi = Math.min(cx - 1, gx | 0);
      let d1 = 1e9, d2 = 1e9, best = 0;
      for (let j = -1; j <= 1; j++) {
        let jy = cyi + j, wy = 0;
        if (jy < 0) { jy += cy; wy = -cy; } else if (jy >= cy) { jy -= cy; wy = cy; }
        const row = jy * cx;
        for (let i = -1; i <= 1; i++) {
          let ix = cxi + i, wx = 0;
          if (ix < 0) { ix += cx; wx = -cx; } else if (ix >= cx) { ix -= cx; wx = cx; }
          const k = row + ix;
          const dx = px[k] + wx - gx, dy = py[k] + wy - gy;
          const d = dx * dx + dy * dy;
          if (d < d1) { d2 = d1; d1 = d; best = k; } else if (d < d2) { d2 = d; }
        }
      }
      f1[o + x] = Math.sqrt(d1);
      f2[o + x] = Math.sqrt(d2);
      id[o + x] = cid[best];
    }
  }
  if (size === fullSize) return { f1, f2, id };
  return {
    f1: upsample(f1, size, fullSize, true),
    f2: upsample(f2, size, fullSize, true),
    id: upsample(id, size, fullSize, false),
  };
}

/** Wrapping upsample; `smooth` picks bilinear, otherwise nearest (for ids). */
function upsample(src, res, size, smooth) {
  const out = new Float32Array(size * size);
  if (smooth) { upAdd(out, size, src, res, res, 1); return out; }
  const s = res / size;
  for (let y = 0; y < size; y++) {
    const r = Math.min(res - 1, (y * s) | 0) * res, o = y * size;
    for (let x = 0; x < size; x++) out[o + x] = src[r + Math.min(res - 1, (x * s) | 0)];
  }
  return out;
}

/**
 * Per-surface build context. Presets get one of these and paint into the
 * linear-space channel arrays directly.
 */
class Builder {
  constructor(size, seed) {
    this.size = size;
    this.n = size * size;
    this.seed = seed;
    this.noise = makeNoise(seed);
    this.rand = mulberry(seed ^ 0x9e3779b9);
    this.R = new Float32Array(this.n);
    this.G = new Float32Array(this.n);
    this.B = new Float32Array(this.n);
    this.rough = new Float32Array(this.n).fill(0.8);
    this.ao = new Float32Array(this.n).fill(1);
    this.metal = new Float32Array(this.n);
    this.h = new Float32Array(this.n);
  }

  /**
   * fbm in [-1,1]. fx/fy are lattice cell counts across the tile (anisotropic).
   * `capRes` caps the resolution any single octave is evaluated at; the shared
   * detail layer covers everything finer, so capping the micro bands is free.
   */
  fbm(fx, fy, oct = 5, gain = 0.5, lac = 2, capRes = 0) {
    return fbmField(this.noise, this.size, this.n, fx, fy, oct, gain, lac, capRes);
  }

  /** Ridged multifractal — good for crack spines and rock. */
  ridged(fx, fy, oct = 4) {
    const out = this.fbm(fx, fy, oct, 0.55);
    for (let i = 0; i < out.length; i++) out[i] = 1 - Math.abs(out[i]) * 2;
    return out;
  }

  worley(cx, cy, jitter = 1, salt = 0) {
    return worley((this.seed * 2654435761 + salt * 40503) >>> 0, this.size, cx, cy, jitter);
  }

  blur(src, radius, passes = 2) { return blur(src, this.size, radius, passes); }

  /** Domain-warp `src` by the vector field (dx, dy), amplitude in texels. */
  warp(src, dx, dy, amt) {
    const size = this.size;
    const out = new Float32Array(this.n);
    for (let y = 0; y < size; y++) {
      const o = y * size;
      for (let x = 0; x < size; x++) {
        const i = o + x;
        let sx = x + dx[i] * amt, sy = y + dy[i] * amt;
        sx -= Math.floor(sx / size) * size;
        sy -= Math.floor(sy / size) * size;
        const x0 = sx | 0, y0 = sy | 0;
        const tx = sx - x0, ty = sy - y0;
        const x1 = (x0 + 1) % size, y1 = (y0 + 1) % size;
        const r0 = y0 * size, r1 = y1 * size;
        const a = src[r0 + x0], t0 = a + (src[r0 + x1] - a) * tx;
        const c = src[r1 + x0], t1 = c + (src[r1 + x1] - c) * tx;
        out[i] = t0 + (t1 - t0) * ty;
      }
    }
    return out;
  }

  /** Convenience: paint a linear RGB triple at index i. */
  set(i, r, g, b) { this.R[i] = r; this.G[i] = g; this.B[i] = b; }
  tint(i, r, g, b, t) {
    this.R[i] += (r - this.R[i]) * t;
    this.G[i] += (g - this.G[i]) * t;
    this.B[i] += (b - this.B[i]) * t;
  }
  scale(i, k) { this.R[i] *= k; this.G[i] *= k; this.B[i] *= k; }
}

/**
 * Height -> tangent-space normal. Scharr kernel: less axis-aligned aliasing
 * than a plain Sobel, which matters a lot on the road at grazing angles.
 */
function normalFromHeight(height, size, strength) {
  const data = new Uint8Array(size * size * 4);
  // Per-texel gradients shrink as resolution rises for a fixed physical
  // feature, so the slope gain has to track the texture size.
  const k = strength * (size / 512) * 0.25;
  for (let y = 0; y < size; y++) {
    const ym = wrapi(y - 1, size) * size, y0 = y * size, yp = wrapi(y + 1, size) * size;
    for (let x = 0; x < size; x++) {
      const xm = wrapi(x - 1, size), xp = wrapi(x + 1, size);
      const tl = height[ym + xm], t = height[ym + x], tr = height[ym + xp];
      const l = height[y0 + xm], r = height[y0 + xp];
      const bl = height[yp + xm], b = height[yp + x], br = height[yp + xp];
      const nx = -((tr + 2 * r + br) - (tl + 2 * l + bl)) * k;
      const ny = -((bl + 2 * b + br) - (tl + 2 * t + tr)) * k;
      const len = Math.sqrt(nx * nx + ny * ny + 1);
      const i = (y * size + x) * 4;
      data[i] = clamp(nx / len * 0.5 + 0.5, 0, 1) * 255;
      data[i + 1] = clamp(ny / len * 0.5 + 0.5, 0, 1) * 255;
      data[i + 2] = clamp(1 / len * 0.5 + 0.5, 0, 1) * 255;
      data[i + 3] = 255;
    }
  }
  return data;
}

/** Cavity AO derived from the height field: how far below the local mean a texel sits. */
function cavityAO(height, size, radius, strength) {
  const wide = blur(height, size, radius, 2);
  const out = new Float32Array(size * size);
  for (let i = 0; i < out.length; i++) {
    out[i] = clamp(1 - Math.max(0, wide[i] - height[i]) * strength, 0.15, 1);
  }
  return out;
}

/* --------------------------------------------------------------- factory -- */

const cache = new Map();
// Level.js registers the same spec under several names (dirt/sandbag,
// road/asphalt). Keying a second cache on the spec object means the build and
// the GPU upload happen once.
const specCache = new WeakMap();
const cloneCache = new Map();
let detailSet = null;

let genMs = 0;

export class TextureFactory {
  constructor(renderer, size = 512) {
    this.renderer = renderer;
    this.size = size;
    this.maxAniso = renderer ? renderer.capabilities.getMaxAnisotropy() : 8;
  }

  _texFromData(data, size, { srgb = false, repeat = 1, aniso = true } = {}) {
    const tex = new THREE.DataTexture(
      data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType
    );
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(repeat, repeat);
    tex.anisotropy = aniso ? this.maxAniso : 1;
    tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    // CanvasTexture used to flip; keep the same orientation so "down the wall"
    // stays down for the directional streak fields.
    tex.flipY = true;
    tex.generateMipmaps = true;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.needsUpdate = true;
    return tex;
  }

  /**
   * Millimetre-scale detail layer shared by every surface. Sampled at a high
   * repeat and cross-faded out with distance, it keeps 30cm views crisp
   * without adding any shimmer at range.
   */
  _detail() {
    if (detailSet) return detailSet;
    const t0 = performance.now();
    const size = 256;
    const B = new Builder(size, 60613);
    const grit = B.fbm(size / 3, size / 3, 2, 0.55);
    const mid = B.fbm(size / 12, size / 12, 3, 0.5);
    const cell = B.worley(48, 48, 1, 3);

    const aData = new Uint8Array(size * size * 4);
    const h = new Float32Array(size * size);
    for (let i = 0; i < h.length; i++) {
      const pit = sstep(0.55, 0.12, cell.f1[i]) * (hash1(cell.id[i] * 91.3) > 0.62 ? 1 : 0);
      h[i] = grit[i] * 0.6 + mid[i] * 0.4 - pit * 0.5;
    }
    const nData = normalFromHeight(h, size, 6.0);
    for (let i = 0; i < h.length; i++) {
      const v = clamp(0.5 + h[i] * 0.16, 0, 1);
      const o = i * 4;
      aData[o] = v * 255; aData[o + 1] = v * 255; aData[o + 2] = v * 255;
      aData[o + 3] = clamp(0.5 + (mid[i] * 0.5 + grit[i] * 0.5) * 0.5, 0, 1) * 255;
    }
    detailSet = {
      normalMap: this._texFromData(nData, size),
      albedoMap: this._texFromData(aData, size),
    };
    genMs += performance.now() - t0;
    return detailSet;
  }

  /**
   * @param {string} name  cache key
   * @param {object} spec  either { build(Builder) } (preferred) or the legacy
   *                       per-pixel callback form { albedo, height, rough, ... }
   */
  surface(name, spec) {
    if (cache.has(name)) return cache.get(name);
    const size = spec.size ?? this.size;
    const alias = specCache.get(spec);
    if (alias && alias.size === size) { cache.set(name, alias); return alias; }
    const t0 = performance.now();
    const B = new Builder(size, spec.seed ?? 1337);

    if (spec.build) spec.build(B);
    else legacyBuild(B, spec);

    const n = size * size;
    const albedo = new Uint8Array(n * 4);
    const orm = new Uint8Array(n * 4);
    for (let i = 0; i < n; i++) {
      const o = i * 4;
      albedo[o] = toSRGB(B.R[i]);
      albedo[o + 1] = toSRGB(B.G[i]);
      albedo[o + 2] = toSRGB(B.B[i]);
      albedo[o + 3] = 255;
      orm[o] = clamp(B.ao[i], 0, 1) * 255;
      orm[o + 1] = clamp(B.rough[i], 0.04, 1) * 255;
      orm[o + 2] = clamp(B.metal[i], 0, 1) * 255;
      orm[o + 3] = 255;
    }

    const normal = normalFromHeight(B.h, size, spec.normalStrength ?? 2.2);

    const set = {
      map: this._texFromData(albedo, size, { srgb: true }),
      normalMap: this._texFromData(normal, size),
      ormMap: this._texFromData(orm, size),
      size,
    };
    cache.set(name, set);
    specCache.set(spec, set);
    const dt = performance.now() - t0;
    genMs += dt;
    if (typeof window !== 'undefined') {
      window.__matGenMs = Math.round(genMs);
      (window.__matGen ||= {})[name] = Math.round(dt);
    }
    return set;
  }

  _repeated(name, set, key, repeat) {
    if (repeat === 1) return set[key];
    const ck = `${set[key].uuid}|${repeat}`;
    if (cloneCache.has(ck)) return cloneCache.get(ck);
    const t = set[key].clone();
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(repeat, repeat);
    t.colorSpace = set[key].colorSpace;
    t.anisotropy = set[key].anisotropy;
    t.needsUpdate = true;
    cloneCache.set(ck, t);
    return t;
  }

  /** Build a MeshStandardMaterial wired to a generated surface set. */
  material(name, spec, matOpts = {}) {
    const set = this.surface(name, spec);
    const repeat = matOpts.repeat ?? 1;
    const map = this._repeated(name, set, 'map', repeat);
    const normalMap = this._repeated(name, set, 'normalMap', repeat);
    const orm = this._repeated(name, set, 'ormMap', repeat);

    const useDetail = matOpts.detail !== false;
    const d = useDetail ? this._detail() : null;

    return new DetailStandardMaterial({
      map,
      normalMap,
      normalScale: new THREE.Vector2(matOpts.normalScale ?? 1, matOpts.normalScale ?? 1),
      aoMap: orm,
      roughnessMap: orm,
      metalnessMap: orm,
      roughness: matOpts.roughness ?? 1,
      metalness: matOpts.metalness ?? 1,
      envMapIntensity: matOpts.envMapIntensity ?? 1,
      color: matOpts.color ?? 0xffffff,
      detail: d && {
        normalMap: d.normalMap,
        albedoMap: d.albedoMap,
        repeat: matOpts.detailRepeat ?? spec.detailRepeat ?? 6,
        normalScale: matOpts.detailNormalScale ?? spec.detailNormalScale ?? 0.85,
        albedoScale: matOpts.detailAlbedoScale ?? spec.detailAlbedoScale ?? 0.55,
        roughScale: matOpts.detailRoughScale ?? spec.detailRoughScale ?? 0.22,
        fade: matOpts.detailFade ?? spec.detailFade ?? [5, 18],
      },
      ...matOpts.extra,
    });
  }
}

/* ------------------------------------------------- detail-map material ---- */

/**
 * MeshStandardMaterial + a second, much higher-frequency normal/albedo/roughness
 * layer that fades out with view distance. Implemented as a subclass rather than
 * a bare onBeforeCompile so that `.clone()` (used all over Level.js) keeps it.
 */
class DetailStandardMaterial extends THREE.MeshStandardMaterial {
  constructor(params = {}) {
    const { detail, ...rest } = params;
    super(rest);
    this._detail = detail || null;
    this._installDetail();
  }

  copy(source) {
    super.copy(source);
    this._detail = source._detail || null;
    this._installDetail();
    return this;
  }

  _installDetail() {
    const d = this._detail;
    if (!d) { this.onBeforeCompile = THREE.Material.prototype.onBeforeCompile; return; }
    this.onBeforeCompile = (shader) => {
      shader.uniforms.detailNormalMap = { value: d.normalMap };
      shader.uniforms.detailAlbedoMap = { value: d.albedoMap };
      shader.uniforms.detailRepeat = { value: d.repeat };
      shader.uniforms.detailNormalScale = { value: d.normalScale };
      shader.uniforms.detailAlbedoScale = { value: d.albedoScale };
      shader.uniforms.detailRoughScale = { value: d.roughScale };
      shader.uniforms.detailFade = { value: new THREE.Vector2(d.fade[0], d.fade[1]) };

      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', `#include <common>
uniform sampler2D detailNormalMap;
uniform sampler2D detailAlbedoMap;
uniform float detailRepeat;
uniform float detailNormalScale;
uniform float detailAlbedoScale;
uniform float detailRoughScale;
uniform vec2 detailFade;`)
        .replace('#include <map_fragment>', `
	float dFade = 1.0 - smoothstep( detailFade.x, detailFade.y, length( vViewPosition ) );
	vec4 dDetail = vec4( 0.5 );
	#ifdef USE_MAP
		dDetail = texture2D( detailAlbedoMap, vMapUv * detailRepeat );
	#endif
	#include <map_fragment>
	diffuseColor.rgb *= mix( vec3( 1.0 ), dDetail.rgb * 2.0, dFade * detailAlbedoScale );`)
        .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
	roughnessFactor = clamp( roughnessFactor + ( dDetail.a - 0.5 ) * detailRoughScale * dFade, 0.045, 1.0 );`)
        .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
	#ifdef USE_NORMALMAP_TANGENTSPACE
	{
		vec3 dN = texture2D( detailNormalMap, vNormalMapUv * detailRepeat ).xyz * 2.0 - 1.0;
		float k = detailNormalScale * dFade;
		normal = normalize( normal + tbn[ 0 ] * ( dN.x * k ) + tbn[ 1 ] * ( dN.y * k ) );
	}
	#endif`);
    };
  }
}

/* ------------------------------------------------------- legacy support --- */

/** Old per-pixel-callback spec form, kept so third-party specs keep working. */
function legacyBuild(B, spec) {
  const size = B.size;
  const period = spec.period ?? 8;
  const base = B.fbm(period, period, spec.octaves ?? 5, spec.gain ?? 0.5);
  const det = B.fbm(period * 5, period * 5, 3, 0.5);
  for (let i = 0; i < B.n; i++) {
    const x = (i % size) / size, y = ((i / size) | 0) / size;
    const n = base[i] * 0.5 + 0.5;
    const d = det[i] * 0.5 + 0.5;
    const h = spec.height ? spec.height(n, x, y, d) : n;
    B.h[i] = h;
    const c = spec.albedo(n, x, y, d, h);
    B.R[i] = c[0]; B.G[i] = c[1]; B.B[i] = c[2];
    B.rough[i] = spec.rough ? spec.rough(n, d, h) : 0.8;
    B.ao[i] = spec.ao ? spec.ao(n, d, h) : 1;
    B.metal[i] = spec.metal ? spec.metal(n, d, h) : 0;
  }
}

/* ------------------------------------------------------------ presets ---- */
/*
 * Linear albedo reference used below:
 *   fresh concrete 0.35-0.45 | weathered concrete 0.28-0.36
 *   asphalt (new) 0.04-0.06  | asphalt (weathered) 0.08-0.12
 *   dry sand 0.30-0.40       | red brick 0.10-0.18
 *   painted render 0.50-0.70 | iron oxide 0.08-0.20
 *   polished steel F0 ~0.56  | phosphate/parkerised steel ~0.05
 */

/** Running-bond brick, in tile-normalised units. Returns {mortar, id, h}. */
function brickField(B, rowH, brickL, mortar) {
  const size = B.size;
  const rows = Math.max(2, Math.round(1 / rowH));
  const cols = Math.max(2, Math.round(1 / brickL));
  const mortarMask = new Float32Array(B.n);
  const id = new Float32Array(B.n);
  const jitter = B.fbm(24, 24, 3, 0.5);
  for (let y = 0; y < size; y++) {
    const v = y / size;
    const rf = v * rows;
    const row = Math.floor(rf);
    const ry = rf - row;
    const offset = (row & 1) * 0.5;
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      const j = jitter[i] * 0.06;
      const cf = (x / size + offset) * cols + j;
      const col = Math.floor(cf);
      const cxr = cf - col;
      const mv = Math.min(sstep(0, mortar * rows, ry), sstep(0, mortar * rows, 1 - ry));
      const mh = Math.min(sstep(0, mortar * cols, cxr), sstep(0, mortar * cols, 1 - cxr));
      mortarMask[i] = 1 - Math.min(mv, mh); // 1 in the mortar joint
      id[i] = hash1(row * 13.7 + col * 7.31 + 0.5);
    }
  }
  return { mortar: mortarMask, id };
}

export const SURFACES = {
  /* ---------------------------------------------------------- concrete -- */
  /* Level.js unwraps concrete at 1.4-2.4 m per repeat; the tile is authored to
     read as ~2 m of poured, form-marked, spalled concrete. */
  concrete: {
    seed: 91, size: 1024, normalStrength: 1.9, detailNormalScale: 0.85,
    build(B) {
      const S = B.size, HALF = S >> 1;
      const macro = B.fbm(3, 3, 4, 0.55);
      const wx = B.fbm(5, 5, 3, 0.5), wy = B.fbm(5, 5, 3, 0.5);
      const blotch = B.warp(B.fbm(7, 7, 4, 0.5), wx, wy, S * 0.04);
      const meso = B.fbm(24, 24, 3, 0.5);
      const micro = B.fbm(S / 8, S / 8, 2, 0.5, 2, HALF);
      const agg = B.worley(64, 64, 1, 1);      // exposed aggregate, ~3 cm
      const bub = B.worley(70, 70, 1, 2);      // entrained air voids, ~3 cm
      const bubGate = B.fbm(9, 9, 3, 0.5);     // bugholes cluster, they don't grid
      const spallSrc = B.warp(B.fbm(11, 11, 4, 0.5), wx, wy, S * 0.030);

      // Two horizontal pour lifts per tile plus their bleed lines.
      const pourWobble = B.fbm(9, 2, 2, 0.5);
      const rustStreak = B.fbm(90, 4, 3, 0.5);

      // Form-tie holes on a jittered 2x2 grid: ~25 mm recessed, grout-plugged,
      // each bleeding rust downwards from the tie left in the pour.
      const holes = [];
      for (let gy = 0; gy < 2; gy++) {
        for (let gx = 0; gx < 2; gx++) {
          holes.push([(gx + 0.25 + B.rand() * 0.5) * 0.5, (gy + 0.22 + B.rand() * 0.5) * 0.5]);
        }
      }

      for (let y = 0; y < S; y++) {
        const v = y / S;
        for (let x = 0; x < S; x++) {
          const i = y * S + x;
          const u = x / S;

          // ---- base tone: 0.30-0.38 linear, weathered grey ---------------
          let alb = 0.330 + macro[i] * 0.030 + blotch[i] * 0.024 + meso[i] * 0.012;
          let rgh = 0.70 + meso[i] * 0.05 + micro[i] * 0.05;
          let h = macro[i] * 0.16 + blotch[i] * 0.14 + meso[i] * 0.10 + micro[i] * 0.06;

          // ---- pour lines -----------------------------------------------
          const lift = frac(v * 2 + pourWobble[i] * 0.010);
          const line = sstep(0.020, 0.0, Math.min(lift, 1 - lift));
          alb += (Math.floor(v * 2 + pourWobble[i] * 0.010) & 1 ? 0.011 : -0.008);
          h -= line * 0.32;
          alb *= 1 - line * 0.20;
          rgh += line * 0.10;

          // ---- entrained air voids (bugholes) ---------------------------
          // Sparse, size-varied and clustered where the form was poorly
          // vibrated, rather than one hole per cell.
          const voidSel = hash1(bub.id[i] * 71.3);
          if (voidSel > 0.80) {
            const size = 0.10 + (voidSel - 0.80) * 1.3;   // 0.10 .. 0.36
            const p = sstep(size * 1.9, size * 0.4, bub.f1[i]) *
              sstep(-0.22, 0.12, bubGate[i]);
            h -= p * 0.42;
            alb *= 1 - p * 0.26;
            rgh += p * 0.10;
          }

          // ---- spalling: chipped edges exposing coarse aggregate --------
          const edgeBias = Math.max(line * 0.7, sstep(0.10, 0.0, Math.min(u, 1 - u, v, 1 - v)) * 0.5);
          const sv = spallSrc[i] * 0.5 + 0.5;
          const spall = sstep(0.600 - edgeBias * 0.22, 0.625 - edgeBias * 0.22, sv);
          if (spall > 0.001) {
            const stone = sstep(0.50, 0.14, agg.f1[i]);
            const stoneTone = 0.28 + hash1(agg.id[i] * 33.1) * 0.20;
            const exposed = mix(0.295, stoneTone, stone * 0.85);
            alb = mix(alb, exposed, spall);
            h -= spall * 0.30;
            h += stone * spall * 0.16;
            rgh = mix(rgh, 0.93 - stone * 0.06, spall);
          }

          // ---- form-tie holes + rebar runoff -----------------------------
          const holeR = 0.012;
          let runoff = 0;
          for (let k = 0; k < holes.length; k++) {
            const hx = holes[k][0], hy = holes[k][1];
            let dxu = u - hx; dxu -= Math.round(dxu);
            if (dxu > 0.032 || dxu < -0.032) continue;    // outside hole + runoff
            let dyu = v - hy; dyu -= Math.round(dyu);
            const dist = Math.sqrt(dxu * dxu + dyu * dyu);
            if (dist < holeR * 3.4) {
              const core = sstep(holeR, holeR * 0.4, dist);
              const patch = sstep(holeR * 2.8, holeR * 1.05, dist);
              alb = mix(alb, alb * 1.07 + 0.016, patch * 0.55);   // grout plug
              rgh = mix(rgh, 0.88, patch * 0.55);
              h -= patch * 0.10 + core * 0.55;
              alb = mix(alb, 0.040, core);
              rgh = mix(rgh, 0.95, core);
            }
            let below = v - hy; if (below < 0) below += 1;
            if (below < 0.40) {
              const lateral = sstep(0.030, 0.004, dxu < 0 ? -dxu : dxu) *
                (0.30 + sstep(0.0, 0.45, rustStreak[i]) * 0.70);
              if (lateral > 0.004) {
                const r0 = lateral * Math.exp(-below * 9.5) * sstep(0.0, 0.008, below);
                if (r0 > runoff) runoff = r0;
              }
            }
          }

          // ---- grime settling in the low frequencies --------------------
          const grime = sstep(0.15, -0.45, macro[i] + blotch[i] * 0.5);
          alb *= 1 - grime * 0.16;
          rgh += grime * 0.05;

          B.set(i, alb * 1.00, alb * 0.995, alb * 0.965);
          if (runoff > 0.01) {
            B.tint(i, 0.115, 0.052, 0.024, clamp(runoff * 0.85, 0, 0.70));
            rgh += runoff * 0.12;
          }
          B.rough[i] = clamp(rgh, 0.05, 1);
          B.h[i] = h;
        }
      }
      B.ao.set(cavityAO(B.h, S, 5, 2.2));
    },
  },

  /* ----------------------------------------------------------- asphalt -- */
  /* Level.js unwraps the carriageway at 4.5 m per repeat. */
  asphalt: {
    seed: 4412, size: 1024, normalStrength: 2.1, detailNormalScale: 1.0,
    detailRepeat: 4,
    build(B) {
      const S = B.size, HALF = S >> 1;
      const wx = B.fbm(6, 6, 3, 0.5), wy = B.fbm(6, 6, 3, 0.5);
      const macro = B.fbm(2, 2, 3, 0.55);
      const coarse = B.worley(128, 128, 1, 1);   // ~3.5 cm aggregate
      const fines = B.worley(230, 230, 1, 2);    // ~2 cm fines
      const micro = B.fbm(S / 8, S / 8, 2, 0.5, 2, HALF);

      // Crack network: worley cell walls, domain-warped so they wander, gated
      // by a low-frequency mask so cracking is regional rather than uniform.
      const cracks = B.worley(10, 10, 1, 3);     // ~45 cm plates
      const crackRaw = new Float32Array(B.n);
      for (let i = 0; i < B.n; i++) crackRaw[i] = cracks.f2[i] - cracks.f1[i];
      const crackW = B.warp(crackRaw, wx, wy, S * 0.030);
      const alligator = B.worley(36, 36, 1, 4);  // ~12 cm plates
      const alliRaw = new Float32Array(B.n);
      for (let i = 0; i < B.n; i++) alliRaw[i] = alligator.f2[i] - alligator.f1[i];
      const alliW = B.warp(alliRaw, wx, wy, S * 0.010);
      const crackRegion = B.fbm(3, 3, 3, 0.5);

      // Patch repairs: warped blobs of newer, finer, darker mix.
      const patch = B.warp(B.fbm(2, 2, 3, 0.5), wx, wy, S * 0.05);

      // Traffic polish: strongly anisotropic streaking along +V (the road is
      // unwrapped with V running along the carriageway) — wheel-track burnish.
      const polishN = B.fbm(60, 5, 3, 0.5);
      const polishLane = B.fbm(3, 1, 2, 0.5);    // broad wheel-path bands

      for (let i = 0; i < B.n; i++) {
        // ---- bitumen binder with aggregate bedded into it ----------------
        // Only the crowns of the stones break the binder film; anything more
        // and the surface reads as loose gravel rather than a wearing course.
        const stoneSel = hash1(coarse.id[i] * 47.9);
        const stoneR = 0.18 + stoneSel * 0.15;
        const stone = sstep(stoneR + 0.11, stoneR - 0.02, coarse.f1[i]);
        const fineSel = hash1(fines.id[i] * 17.3);
        const fine = sstep(0.22, 0.06, fines.f1[i]) * (fineSel > 0.62 ? 1 : 0);

        // Mostly dark basalt; a minority of pale limestone chippings.
        const pale = stoneSel > 0.88 ? 1 : 0;
        const stoneTone = mix(0.058 + stoneSel * 0.022, 0.120, pale);
        let alb = 0.038 + macro[i] * 0.006;
        alb = mix(alb, stoneTone, stone * 0.85);
        alb = mix(alb, alb * 1.22, fine * 0.5);

        let rgh = 0.90 - stone * 0.09 - fine * 0.03 + micro[i] * 0.03;
        let h = stone * 0.16 + fine * 0.07 + micro[i] * 0.06 + macro[i] * 0.05;

        // ---- patch repair -----------------------------------------------
        const pv = patch[i] * 0.5 + 0.5;
        const pIn = sstep(0.60, 0.655, pv);
        const pEdge = sstep(0.578, 0.600, pv) * sstep(0.680, 0.655, pv);
        if (pIn > 0) {
          alb = mix(alb, 0.038 + stoneTone * 0.14 * stone, pIn * 0.85);
          rgh = mix(rgh, 0.93, pIn * 0.7);
          h += pIn * 0.06;
        }
        if (pEdge > 0) {
          alb *= 1 - pEdge * 0.45;
          h -= pEdge * 0.26;
          rgh = mix(rgh, 0.95, pEdge);
        }

        // ---- crack network ----------------------------------------------
        const region = sstep(-0.12, 0.24, crackRegion[i]);
        const cw = sstep(0.038, 0.005, crackW[i]) * region;
        const aw = sstep(0.030, 0.005, alliW[i]) * sstep(0.12, 0.42, crackRegion[i]);
        const crack = clamp(Math.max(cw, aw * 0.8), 0, 1);
        if (crack > 0) {
          alb = mix(alb, 0.013, crack);   // genuinely dark interior
          rgh = mix(rgh, 0.96, crack);
          h -= crack * 0.60;
        }

        // ---- traffic polish ---------------------------------------------
        // Burnish only inside the wheel paths, and gently: this is a sheen
        // change, not a colour change. Overdoing it reads as brushed fabric.
        const lane = sstep(-0.06, 0.16, polishLane[i]);
        const polish = clamp(sstep(-0.02, 0.30, polishN[i]) * lane *
          (1 - crack) * (1 - pIn * 0.6), 0, 1);
        rgh = mix(rgh, 0.58, polish * 0.75);
        alb = mix(alb, alb * 1.07 + 0.003, polish * 0.6);
        h = mix(h, h * 0.62, polish * 0.7);

        // slight blue-grey oxidation of the binder
        B.set(i, alb * 0.99, alb * 1.00, alb * 1.06);
        B.rough[i] = clamp(rgh, 0.05, 1);
        B.h[i] = h;
      }
      B.ao.set(cavityAO(B.h, S, 4, 1.6));
    },
  },

  /* -------------------------------------------------------- rust metal -- */
  /* Containers/drums/shutters, unwrapped at 0.4-1.6 m per repeat. Level.js
     models the container ribs as real geometry, so this is a flat panel:
     welded rails, panel joints, paint failing from the seams outwards. */
  rustMetal: {
    seed: 7781, size: 1024, normalStrength: 1.7, detailNormalScale: 0.7,
    detailRepeat: 4,
    build(B) {
      const S = B.size, HALF = S >> 1;
      const wx = B.fbm(6, 6, 3, 0.5), wy = B.fbm(6, 6, 3, 0.5);
      const macro = B.fbm(5, 5, 4, 0.55);
      const flake = B.worley(80, 80, 1, 1);
      const pit = B.worley(130, 130, 1, 2);
      const micro = B.fbm(S / 8, S / 8, 2, 0.5, 2, HALF);
      const streak = B.fbm(85, 4, 3, 0.5, 2, HALF);       // vertical rust runoff
      const dirtStreak = B.fbm(45, 3, 3, 0.5, 2, HALF);
      const rustBlob = B.warp(B.fbm(9, 9, 4, 0.5), wx, wy, S * 0.035);
      const seamWobble = B.fbm(8, 3, 2, 0.5);
      const dent = B.fbm(6, 6, 2, 0.5);

      for (let y = 0; y < S; y++) {
        const v = y / S;
        for (let x = 0; x < S; x++) {
          const i = y * S + x;
          const u = x / S;

          // ---- welded rails / panel joints -------------------------------
          let seam = 0, seamBead = 0;
          for (let k = 0; k < 2; k++) {
            const sy = k === 0 ? 0.07 : 0.93;
            const dy = Math.abs(v - sy + seamWobble[i] * 0.005);
            seam = Math.max(seam, sstep(0.026, 0.004, dy));
            const lump = 0.5 + 0.5 * Math.sin(u * Math.PI * 2 * 52 + seamWobble[i] * 7);
            seamBead = Math.max(seamBead, sstep(0.017, 0.002, dy) * (0.55 + lump * 0.45));
          }
          // vertical panel joint
          const jx = Math.abs(frac(u + 0.5) - 0.5);
          const joint = sstep(0.010, 0.001, jx);

          // ---- rust mask: blooms from the seams, joints and lower edge ----
          const wetness = clamp(seam * 1.0 + joint * 0.6 +
            sstep(0.12, 0.0, Math.min(v, 1 - v)) * 0.7, 0, 1.3);
          const bloom = rustBlob[i] * 0.5 + 0.5;
          let rust = sstep(0.560 - wetness * 0.34, 0.700 - wetness * 0.34, bloom + macro[i] * 0.10);
          const runoff = sstep(0.22, 0.75, streak[i]) *
            Math.exp(-Math.max(0, v - 0.10) * 4.0) * sstep(0.06, 0.11, v);
          rust = clamp(Math.max(rust, runoff * 0.85), 0, 1);
          const heavy = sstep(0.55, 0.95, rust);

          // ---- paint -> chipped bare steel -> oxide -> scale -------------
          // Light neutral base so Level.js's per-object colour tints cleanly.
          const paintWear = sstep(0.30, 0.70, rust);
          let r = 0.50 + macro[i] * 0.030, g = r, b = r * 1.01;
          let rgh = 0.40 + micro[i] * 0.05 + (macro[i] * 0.5 + 0.5) * 0.07;
          let metal = 0.0;
          let h = micro[i] * 0.05 + dent[i] * 0.14 - joint * 0.45 - seam * 0.10;

          const dirt = sstep(0.15, 0.8, dirtStreak[i]) * 0.35;
          r *= 1 - dirt * 0.22; g *= 1 - dirt * 0.22; b *= 1 - dirt * 0.20;
          rgh += dirt * 0.10;

          // bare steel ring where the paint has lifted but rust hasn't taken
          const bare = clamp(paintWear * (1 - sstep(0.40, 0.66, rust)), 0, 1);
          r = mix(r, 0.560, bare); g = mix(g, 0.570, bare); b = mix(b, 0.580, bare);
          rgh = mix(rgh, 0.34 + micro[i] * 0.06, bare);
          metal = mix(metal, 1.0, bare);
          h -= bare * 0.05;

          // oxide: 0.08-0.19 linear iron oxide
          const rustTone = 0.5 + hash1(flake.id[i] * 23.7) * 0.5;
          r = mix(r, mix(0.100, 0.186, rustTone), rust);
          g = mix(g, mix(0.042, 0.080, rustTone), rust);
          b = mix(b, mix(0.019, 0.033, rustTone), rust);
          rgh = mix(rgh, 0.90 + micro[i] * 0.05, rust);
          metal = mix(metal, 0.0, rust);

          // scaly flakes + pitting inside the heavy rust
          const flakeH = sstep(0.55, 0.15, flake.f1[i]) * heavy;
          const pitH = sstep(0.30, 0.05, pit.f1[i]) * heavy * (hash1(pit.id[i] * 5.1) > 0.5 ? 1 : 0);
          h += flakeH * 0.16 - pitH * 0.22;
          rgh += flakeH * 0.04;
          const dark = pitH * 0.55;
          r *= 1 - dark; g *= 1 - dark; b *= 1 - dark;

          // ---- weld bead sits proud of everything ------------------------
          if (seamBead > 0.01) {
            h += seamBead * 0.42;
            const tintT = sstep(0.1, 0.9, seamBead);
            r = mix(r, mix(0.28, 0.15, tintT), seamBead * 0.7 * (1 - rust));
            g = mix(g, mix(0.22, 0.16, tintT), seamBead * 0.7 * (1 - rust));
            b = mix(b, mix(0.13, 0.21, tintT), seamBead * 0.7 * (1 - rust));
            rgh = mix(rgh, 0.60, seamBead * 0.8 * (1 - rust));
            metal = mix(metal, 0.85, seamBead * 0.8 * (1 - rust));
          }

          B.set(i, r, g, b);
          B.rough[i] = clamp(rgh, 0.05, 1);
          B.metal[i] = clamp(metal, 0, 1);
          B.h[i] = h;
        }
      }
      B.ao.set(cavityAO(B.h, S, 5, 1.8));
    },
  },

  /* -------------------------------------------------------------- sand -- */
  /* Terrain is unwrapped at 7 m per repeat; sandbags at ~0.5 m. */
  sand: {
    seed: 202, size: 512, normalStrength: 1.5, detailNormalScale: 0.8,
    detailRepeat: 8,
    build(B) {
      const S = B.size;
      const wx = B.fbm(3, 3, 2, 0.5), wy = B.fbm(3, 3, 2, 0.5);
      const dune = B.fbm(2, 2, 3, 0.55);
      const meso = B.warp(B.fbm(11, 11, 4, 0.5), wx, wy, S * 0.05);
      const micro = B.fbm(S / 6, S / 6, 2, 0.5);
      const grit = B.worley(190, 190, 1, 1);
      const pebble = B.worley(80, 80, 1, 2);     // ~9 cm stones
      const rippleWarp = B.fbm(3, 6, 3, 0.5);
      const scuff = B.fbm(34, 6, 3, 0.5);
      const dry = B.warp(B.fbm(7, 7, 3, 0.5), wx, wy, S * 0.06);
      // Wind-blown drift lanes: strongly anisotropic, the main macro read.
      const drift = B.fbm(6, 34, 3, 0.5);

      for (let y = 0; y < S; y++) {
        const v = y / S;
        for (let x = 0; x < S; x++) {
          const i = y * S + x;
          const u = x / S;
          // Wind ripples: warped so the crests wander instead of striping.
          const rp = Math.sin((u * 26 + rippleWarp[i] * 4.5 + v * 3) * Math.PI * 2);
          const ripple = Math.sign(rp) * Math.pow(Math.abs(rp), 0.7);

          let alb = 0.320 + dune[i] * 0.030 + meso[i] * 0.032 + drift[i] * 0.026 +
            micro[i] * 0.012;
          let h = dune[i] * 0.26 + meso[i] * 0.24 + drift[i] * 0.16 + ripple * 0.07 +
            micro[i] * 0.07;
          let rgh = 0.94 + micro[i] * 0.04;

          // gravel embedded in the dust
          const pSel = hash1(pebble.id[i] * 61.7);
          if (pSel > 0.70) {
            const p = sstep(0.34, 0.12, pebble.f1[i]);
            const tone = 0.24 + hash1(pebble.id[i] * 12.3) * 0.16;
            alb = mix(alb, tone, p * 0.9);
            h += p * 0.20;
            rgh = mix(rgh, 0.78, p);
          }
          // coarse grain
          const gr = sstep(0.5, 0.2, grit.f1[i]);
          h += gr * 0.05;
          rgh -= gr * 0.05;

          // vehicle scuffing exposing darker damp substrate
          const sc = sstep(0.25, 0.8, scuff[i]);
          alb *= 1 - sc * 0.16;
          h -= sc * 0.05;
          // sun-bleached crust on the high ground, damper sand in the hollows
          const cr = sstep(-0.10, 0.35, dry[i]);
          alb *= 0.90 + cr * 0.22;
          rgh += cr * 0.02;

          B.set(i, alb * 1.070, alb * 0.955, alb * 0.760);
          B.rough[i] = clamp(rgh, 0.05, 1);
          B.h[i] = h;
        }
      }
      B.ao.set(cavityAO(B.h, S, 4, 1.4));
    },
  },

  /* ----------------------------------------------------------- plaster -- */
  /* Facade cladding is unwrapped at 2.4 m per repeat: trowel arcs, spalling
     down to the brick backing, water staining running off the sills. */
  plaster: {
    seed: 5150, size: 1024, normalStrength: 1.5, detailNormalScale: 0.65,
    build(B) {
      const S = B.size, HALF = S >> 1;
      const wx = B.fbm(5, 5, 3, 0.5), wy = B.fbm(5, 5, 3, 0.5);
      const macro = B.fbm(3, 3, 4, 0.55);
      const meso = B.fbm(16, 16, 3, 0.5);
      const micro = B.fbm(S / 8, S / 8, 2, 0.5, 2, HALF);

      // Trowel strokes: anisotropic noise domain-warped by a low-frequency
      // field, so the swipes arc the way a plasterer's float actually moves.
      const swx = B.fbm(3, 3, 2, 0.5), swy = B.fbm(3, 3, 2, 0.5);
      const trowel = B.warp(B.fbm(10, 52, 3, 0.5), swx, swy, S * 0.085);
      const trowel2 = B.warp(B.fbm(46, 9, 3, 0.5), swy, swx, S * 0.065);

      // Spalling: warped and hard-thresholded so the edge is a real edge.
      const spallSrc = B.warp(B.fbm(9, 9, 5, 0.5), wx, wy, S * 0.040);
      const chipSrc = B.warp(B.fbm(30, 30, 3, 0.5), wx, wy, S * 0.012);
      const brick = brickField(B, 0.0312, 0.0938, 0.15);   // 75 mm courses

      // Water staining: released from a couple of sill sources per tile.
      const streak = B.fbm(110, 3, 3, 0.5, 2, HALF);
      const streakBreak = B.fbm(26, 11, 3, 0.5);
      const sills = [];
      for (let k = 0; k < 3; k++) sills.push([B.rand(), B.rand() * 0.9, 0.030 + B.rand() * 0.045]);

      for (let y = 0; y < S; y++) {
        const v = y / S;
        for (let x = 0; x < S; x++) {
          const i = y * S + x;
          const u = x / S;

          // ---- painted render, ~0.58 linear -------------------------------
          let alb = 0.580 + macro[i] * 0.022 + meso[i] * 0.014;
          let rgh = 0.68 + micro[i] * 0.05 + (meso[i] * 0.5 + 0.5) * 0.08;
          let h = macro[i] * 0.10 + meso[i] * 0.08 + micro[i] * 0.05;

          // trowel arcs — tiny in height, very visible in the specular
          const tr = trowel[i] * 0.7 + trowel2[i] * 0.3;
          h += tr * 0.24;
          rgh += tr * 0.06;
          alb *= 1 + tr * 0.022;

          let r = alb * 1.010, g = alb * 0.985, b = alb * 0.930;

          // ---- spalling exposing brickwork --------------------------------
          const sv = spallSrc[i] * 0.5 + 0.5;
          const spall = sstep(0.648, 0.665, sv);
          const rim = sstep(0.628, 0.648, sv) * sstep(0.706, 0.670, sv);
          // shallow surface chips: paint gone, render still there
          const chip = sstep(0.60, 0.64, chipSrc[i] * 0.5 + 0.5) * (1 - spall);
          if (chip > 0) {
            const k = 1 - chip * 0.13;
            r *= k; g *= k * 0.99; b *= k * 0.97;
            rgh = mix(rgh, 0.90, chip);
            h -= chip * 0.10;
          }
          if (spall > 0) {
            // Render fails in two stages. Stage one is much the more common:
            // the finish coat lets go and the grey cementitious scratch coat
            // shows through, still carrying the brick joints as a relief.
            const m = brick.mortar[i];
            const bt = hash1(brick.id[i] * 29.3);
            const sr = 0.330 + bt * 0.035;
            r = mix(r, sr * 1.00, spall);
            g = mix(g, sr * 0.985, spall);
            b = mix(b, sr * 0.950, spall);
            rgh = mix(rgh, 0.90, spall);
            h -= spall * 0.34;
            h -= m * spall * 0.18;

            // Stage two: the backing is gone too and the brick is bare. Rare,
            // small, and only in the middle of a stage-one patch.
            const deep = sstep(0.712, 0.738, sv) * spall;
            if (deep > 0) {
              // fired clay brick 0.10-0.18 linear; lime mortar ~0.30
              const br = mix(0.150 + bt * 0.055, 0.300, m);
              const bg = mix(0.072 + bt * 0.026, 0.292, m);
              const bb = mix(0.050 + bt * 0.017, 0.275, m);
              r = mix(r, br, deep); g = mix(g, bg, deep); b = mix(b, bb, deep);
              rgh = mix(rgh, mix(0.86, 0.93, m), deep);
              h -= deep * 0.30 + m * deep * 0.22;
              h += (hash1(brick.id[i] * 7.7) - 0.5) * 0.07 * deep * (1 - m);
            }
          }
          if (rim > 0) {
            h += rim * 0.14;                     // crumbling lip of the render
            r *= 1 - rim * 0.13; g *= 1 - rim * 0.13; b *= 1 - rim * 0.12;
            rgh = mix(rgh, 0.92, rim);
          }

          // Patch repairs and limewash go the other way: real weathered render
          // has both lighter and darker areas, which is what stops thresholded
          // spalling from reading as camouflage at a distance.
          const patch = sstep(0.395, 0.372, sv);
          if (patch > 0) {
            r = mix(r, 0.660, patch * 0.8);
            g = mix(g, 0.650, patch * 0.8);
            b = mix(b, 0.615, patch * 0.8);
            rgh = mix(rgh, 0.80, patch * 0.7);
            h += patch * 0.10;
          }

          // ---- water streaks off the sills --------------------------------
          let wet = 0;
          for (let k = 0; k < sills.length; k++) {
            const sx = sills[k][0], sy = sills[k][1], wdt = sills[k][2];
            let dxs = u - sx; dxs -= Math.round(dxs);
            if (dxs < 0) dxs = -dxs;
            if (dxs > wdt) continue;
            let dys = v - sy; if (dys < 0) dys += 1;
            const lateral = sstep(wdt, wdt * 0.15, dxs);
            const run = Math.exp(-dys * 3.8) * sstep(0.0, 0.02, dys);
            if (lateral * run > wet) wet = lateral * run;
          }
          const streakMask = sstep(0.05, 0.65, streak[i]) * sstep(-0.5, 0.3, streakBreak[i]);
          const stain = clamp(wet * (0.30 + streakMask * 0.85), 0, 0.75);
          if (stain > 0) {
            const k = 1 - stain * 0.40;
            r *= k; g *= k * 1.005; b *= k * 0.985;
            rgh += stain * 0.10;
          }
          // faint general grime
          const grime = sstep(0.20, -0.4, macro[i]) * 0.28;
          r *= 1 - grime * 0.13; g *= 1 - grime * 0.12; b *= 1 - grime * 0.10;

          B.set(i, r, g, b);
          B.rough[i] = clamp(rgh, 0.05, 1);
          B.h[i] = h;
        }
      }
      B.ao.set(cavityAO(B.h, S, 6, 2.4));
    },
  },

  /* ---------------------------------------------------------- gunmetal -- */
  /* Parkerised steel: bead-blast tooth, machining passes, honest edge wear. */
  gunmetal: {
    seed: 8123, size: 512, normalStrength: 0.9, detailNormalScale: 0.5,
    detailRepeat: 3, detailAlbedoScale: 0.30,
    build(B) {
      const S = B.size;
      const macro = B.fbm(4, 4, 3, 0.5);
      const blast = B.worley(200, 200, 1, 1);
      const micro = B.fbm(S / 4, S / 4, 2, 0.5);
      const machining = B.fbm(2, S / 3, 2, 0.5);   // turning / broach lines
      const wearSrc = B.fbm(11, 11, 4, 0.55);
      const scratch = B.fbm(150, 6, 2, 0.5);

      for (let i = 0; i < B.n; i++) {
        const bl = sstep(0.55, 0.15, blast.f1[i]);
        let alb = 0.042 + macro[i] * 0.010 + micro[i] * 0.006;
        let rgh = 0.46 + bl * 0.10 + micro[i] * 0.05 + macro[i] * 0.04;
        let h = bl * 0.22 + micro[i] * 0.30 + machining[i] * 0.22;

        rgh += machining[i] * 0.05;

        // phosphate worn back to bright steel on the handled edges
        const wear = sstep(0.36, 0.64, wearSrc[i]);
        alb = mix(alb, 0.520, wear * 0.9);
        rgh = mix(rgh, 0.20 + micro[i] * 0.04, wear * 0.9);
        h += wear * 0.05;

        // hairline scratches
        const sc = sstep(0.72, 0.95, scratch[i]);
        alb = mix(alb, 0.45, sc * 0.7);
        rgh = mix(rgh, 0.24, sc * 0.7);
        h -= sc * 0.10;

        B.set(i, alb * 0.98, alb * 1.00, alb * 1.06);
        B.rough[i] = clamp(rgh, 0.06, 1);
        B.metal[i] = 1;
        B.h[i] = h;
      }
      B.ao.set(cavityAO(B.h, S, 3, 1.0));
    },
  },

  /* -------------------------------------------------- extra presets ----- */

  /** Exposed brick wall — running bond, blown pointing, efflorescence. */
  brick: {
    seed: 3311, size: 1024, normalStrength: 2.0, detailNormalScale: 0.8,
    build(B) {
      const S = B.size, HALF = S >> 1;
      const bf = brickField(B, 0.0312, 0.0938, 0.15);
      const macro = B.fbm(4, 4, 4, 0.55);
      const meso = B.fbm(22, 22, 3, 0.5);
      const micro = B.fbm(S / 8, S / 8, 2, 0.5, 2, HALF);
      const pit = B.worley(170, 170, 1, 1);
      const eff = B.fbm(70, 4, 3, 0.5);

      for (let i = 0; i < B.n; i++) {
        const m = bf.mortar[i];
        const t = hash1(bf.id[i] * 29.3);
        const t2 = hash1(bf.id[i] * 3.17);
        let r = mix(0.110 + t * 0.070, 0.290 + macro[i] * 0.03, m);
        let g = mix(0.047 + t * 0.030 + t2 * 0.012, 0.282, m);
        let b = mix(0.033 + t * 0.018, 0.268, m);
        let rgh = mix(0.84 + t2 * 0.06, 0.93, m) + micro[i] * 0.04;
        let h = (1 - m) * 0.55 + (t - 0.5) * 0.10 + micro[i] * 0.06 + meso[i] * 0.05;
        const p = sstep(0.28, 0.06, pit.f1[i]) * (hash1(pit.id[i] * 8.3) > 0.6 ? 1 : 0);
        h -= p * 0.30 * (1 - m * 0.4);
        r *= 1 - p * 0.2; g *= 1 - p * 0.18; b *= 1 - p * 0.16;
        const e = sstep(0.35, 0.9, eff[i]) * m * 0.7;
        r = mix(r, 0.55, e); g = mix(g, 0.55, e); b = mix(b, 0.54, e);
        rgh = mix(rgh, 0.95, e);
        B.set(i, r, g, b);
        B.rough[i] = clamp(rgh, 0.05, 1);
        B.h[i] = h;
      }
      B.ao.set(cavityAO(B.h, S, 5, 2.4));
    },
  },

  /** Clean-ish painted sheet steel — signage, vehicle panels, shutters. */
  paintedSteel: {
    seed: 1207, size: 512, normalStrength: 1.1, detailNormalScale: 0.5,
    build(B) {
      const S = B.size;
      const macro = B.fbm(5, 5, 4, 0.55);
      const micro = B.fbm(S / 4, S / 4, 2, 0.5);
      const orangePeel = B.worley(110, 110, 1, 1);
      const chip = B.fbm(28, 28, 4, 0.55);
      const scratch = B.fbm(130, 5, 2, 0.5);

      for (let i = 0; i < B.n; i++) {
        const peel = sstep(0.6, 0.2, orangePeel.f1[i]);
        let alb = 0.470 + macro[i] * 0.030;
        let rgh = 0.34 + peel * 0.05 + micro[i] * 0.04;
        let metal = 0;
        let h = peel * 0.10 + micro[i] * 0.10;

        const c = sstep(0.54, 0.70, chip[i]);
        alb = mix(alb, 0.130, c);           // primer / oxidised steel beneath
        rgh = mix(rgh, 0.80, c);
        h -= c * 0.20;

        const sc = sstep(0.70, 0.94, scratch[i]);
        alb = mix(alb, 0.520, sc * 0.6);
        rgh = mix(rgh, 0.28, sc * 0.6);
        metal = mix(metal, 1.0, sc * 0.6);

        B.set(i, alb, alb, alb);
        B.rough[i] = clamp(rgh, 0.05, 1);
        B.metal[i] = metal;
        B.h[i] = h;
      }
      B.ao.set(cavityAO(B.h, S, 3, 1.2));
    },
  },
};
